import { useNavigate } from 'react-router-dom';
import { PermissionsUtil } from '../../../utils/permissions/permissionsUtil';
import { PERMISSIONS } from '../../../constants/permissions/Permissions.constants';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { RoutingValues } from '../../../types/Shared.types';
import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { SupplierType } from '../../../types/Supplier.types';
import { getMaxSortOrderValue } from '../../../store/suppliers';
import { useGetTermSetQuery } from '../../../services/i18n/i18n.service';
import { SUPPLY_CHAIN_DEFS } from '../../../constants/i18n/translations/termSetDefinitions/supply-chain';
import { skipToken } from '@reduxjs/toolkit/query';
import SettingsContext from '../../../contexts/settings.context';
import { ColDef } from 'ag-grid-community';
import DefaultColumnTypes from '../../../components/grids/columns/Column.constants';
import CustomHeader from '../../../components/grids/CustomHeader';
import { isNilOrEmpty } from '../../../utils/objectUtils';
import { SupplierTypeGridDefs } from '../../../constants/i18n/translations/termDefinitions/supply-chain';
import {
    useListSupplierTypeQuery,
    useUpdateListSupplierTypeMutation,
} from '../../../services/supplier/supplier.service';
import { auditableGridColumns } from '../../grid/utils/auditableGrid/auditableGridColumns';

const UseSupplierTypeGrid = () => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user);
    const navigate = useNavigate();

    const { data, isLoading } = useListSupplierTypeQuery();
    const [updateList] = useUpdateListSupplierTypeMutation();

    const [colDefs, setColDefs] = useState([]);
    const [rowData, setRowData] = useState([]);

    const { settings } = useContext(SettingsContext);
    const { data: termSet, isLoading: isLoadingTermSet } = useGetTermSetQuery(
        settings?.userSettings
            ? {
                  languageId: settings?.userSettings?.languageId,
                  code: SUPPLY_CHAIN_DEFS.SUPPLIER_TYPE_GRID,
              }
            : skipToken
    );
    const [hiddenIdColumns, auditableColumns] = auditableGridColumns();

    const canCreate = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.SUPPLIERS.SETUP.SUPPLIER_TYPES.CREATE
    );
    const canView = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.SUPPLIERS.SETUP.SUPPLIER_TYPES.VIEW
    );
    const canUpdate = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.SUPPLIERS.SETUP.SUPPLIER_TYPES.EDIT
    );

    const handleGridEdits = (editedRows: SupplierType[]) => {
        updateList(editedRows);
    };

    const redirectToForm = (row: any) => {
        if (row.id) {
            navigate(`/suppliers/setup/type/${row.id}`);
        } else {
            navigate(`/suppliers/setup/type/${RoutingValues.newId}`);
        }
    };

    useEffect(() => {
        let maxValue = 1;
        if (data && data.length > 0) {
            const list = _.maxBy(
                data,
                (typeObj: SupplierType) => typeObj.sortOrder
            );
            maxValue = list.sortOrder + 1;
        }
        dispatch(
            getMaxSortOrderValue({ isLoading: false, maxValue: maxValue })
        );
    }, data);

    useEffect(() => {
        if (!isNilOrEmpty(termSet)) {
            const typeColumnDefs: ColDef[] = [
                ...hiddenIdColumns,
                {
                    field: 'sortOrder',
                    sort: 'asc',
                    ...DefaultColumnTypes.NumberOnly,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={SupplierTypeGridDefs.SortOrder}
                            />
                        );
                    },
                },
                {
                    field: 'code',
                    ...DefaultColumnTypes.ShortText,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={SupplierTypeGridDefs.Code}
                            />
                        );
                    },
                },
                {
                    field: 'typeActive',
                    ...DefaultColumnTypes.Boolean,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={SupplierTypeGridDefs.TypeActive}
                            />
                        );
                    },
                },
                {
                    field: 'default',
                    ...DefaultColumnTypes.Boolean,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={SupplierTypeGridDefs.Default}
                            />
                        );
                    },
                },
                {
                    field: 'emergencyResponse',
                    ...DefaultColumnTypes.Boolean,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={SupplierTypeGridDefs.EmergencyResponse}
                            />
                        );
                    },
                },
                {
                    field: 'description',
                    ...DefaultColumnTypes.LongText,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={SupplierTypeGridDefs.Description}
                            />
                        );
                    },
                },
                ...auditableColumns,
            ];
            setColDefs(typeColumnDefs);
        }
    }, [termSet]);

    useEffect(() => {
        if (!isNilOrEmpty(data)) {
            setRowData(data);
        }
    }, [data]);

    return {
        rowData,
        isLoading,
        canCreate,
        canView,
        canUpdate,
        redirectToForm,
        handleGridEdits,
        termSet,
        colDefs,
        isLoadingTermSet,
    };
};

export default UseSupplierTypeGrid;
